// Access to the module reference data in src/course-categories.json.
//
// The file groups modules under the course category they are taught in, and each
// entry is { name, code } with an optional "multiPart" flag:
//
//   "BSc Level 4-HSC (Sem 1)": [
//     { "name": "Society and Self across the Lifespan", "code": "HSC40222", "multiPart": true },
//     ...
//   ]
//
// One module can be listed under several categories - FDY009 runs in both Foundation-Sep26
// semesters - so the index is keyed by code and each module records every category it
// appears in. No rule reads `categories` or `multiPart` yet; they are carried so a later
// rule can, without a second loader.

import rawCategories from "../course-categories.json";
import { levenshtein, nameKey, normaliseTight, fuzzyLimit } from "./courseNameRules";

/** Stop on malformed reference data rather than build an index that is quietly wrong. */
const fail = (where, problem) => {
  throw new Error(`src/course-categories.json cannot be used: ${where} ${problem}.`);
};

/**
 * Build the lookups plus the conflict list.
 *
 * @returns {{
 *   modules: Object<string, {code: string, name: string, multiPart: boolean, categories: string[]}>,
 *   nameKeyToCode: Object<string, string>,
 *   conflicts: Array<{moduleName: string, codeA: string, codeB: string}>,
 *   names: Array<{name: string, code: string, tight: string}>
 * }}
 *
 * A code listed twice must be the same module both times; anything else is a mistake in
 * the file and stops the build, because Fix Format would otherwise write whichever name
 * happened to come first. When one module NAME maps to two different codes that is a
 * data conflict, not a crash: the first code wins the lookup and the pair is recorded
 * for CD-6 to report.
 */
const buildIndex = () => {
  const modules = {};
  const nameKeyToCode = {};
  const conflicts = [];
  const names = [];

  if (!rawCategories || typeof rawCategories !== "object" || Array.isArray(rawCategories)) {
    fail("the top level", "must be an object of category name to module list");
  }

  Object.keys(rawCategories).forEach((category) => {
    const entries = rawCategories[category];
    if (!Array.isArray(entries)) fail(`category "${category}"`, "must be a list of modules");

    entries.forEach((entry, position) => {
      const where = `entry ${position + 1} of "${category}"`;
      const name = entry && typeof entry.name === "string" ? entry.name.trim() : "";
      const code = entry && typeof entry.code === "string" ? entry.code.trim().toUpperCase() : "";
      if (!name || !code) fail(where, 'needs a non-empty "name" and "code"');
      if (entry.multiPart !== undefined && typeof entry.multiPart !== "boolean") {
        fail(where, 'has a "multiPart" that is not true or false');
      }
      const multiPart = entry.multiPart === true;

      const existing = modules[code];
      if (existing) {
        if (existing.name !== name || existing.multiPart !== multiPart) {
          fail(where, `does not match the earlier entry for ${code} ("${existing.name}")`);
        }
        existing.categories.push(category);
        return;
      }
      modules[code] = { code, name, multiPart, categories: [category] };

      const key = nameKey(name);
      if (nameKeyToCode[key]) {
        conflicts.push({ moduleName: name, codeA: nameKeyToCode[key], codeB: code });
        return;
      }

      nameKeyToCode[key] = code;
      // Precomputed so a fuzzy lookup does not re-normalise every name per row.
      names.push({ name, code, tight: normaliseTight(name) });
    });
  });

  return { modules, nameKeyToCode, conflicts, names };
};

// The data is static, so the index is built once per page load.
let cached = null;

export const getModuleIndex = () => {
  if (!cached) cached = buildIndex();
  return cached;
};

/**
 * The closest known module name to `found`, or null when nothing is close enough.
 * Compares on the tight normalised form so punctuation and spacing differences do not
 * count as edits.
 */
export const findClosestModuleName = (found, index = getModuleIndex()) => {
  const tight = normaliseTight(found);
  if (!tight) return null;

  let best = null;
  index.names.forEach((entry) => {
    const distance = levenshtein(tight, entry.tight);
    if (!best || distance < best.distance) best = { ...entry, distance };
  });

  if (!best) return null;

  const limit = fuzzyLimit(best.tight.length);
  return best.distance <= limit ? { ...best, limit } : null;
};

/**
 * Is `found` close enough to `expected` to be the same module, just written wrong?
 * The gate that keeps canonical-name replacement inside "never guess": a mistyped
 * module code would point at a different module entirely, and the distance would be
 * far outside the limit.
 */
export const isSameModuleName = (found, expected) => {
  const foundTight = normaliseTight(found);
  const expectedTight = normaliseTight(expected);
  if (!expectedTight) return false;

  const distance = levenshtein(foundTight, expectedTight);
  return { same: distance <= fuzzyLimit(expectedTight.length), distance };
};
